import { Component, OnInit, Input, inject, signal, effect, computed } from "@angular/core";
import { RouterOutlet, ActivatedRoute, Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { SubmodulesGridComponent } from "../submodules-grid/submodules-grid.component";
import { BreadcrumbComponent, BreadcrumbItem } from "../breadcrumb/breadcrumb.component";
import { Route, Routes } from "@angular/router";
import { PermissionService, SessionService } from "@core";
/**
 * Componente contenedor genérico y reutilizable para mostrar grid de submódulos
 * 
 * Este componente:
 * - Recibe el path de la ruta padre como input o desde route.data
 * - Busca automáticamente las rutas hijas del path especificado desde Router.config
 * - Muestra el grid cuando no hay ruta hija activa
 * - Muestra el router-outlet cuando hay una ruta hija activa
 * 
 * @example
 * ```typescript
 * // En configuration.routes.ts
 * {
 *   path: 'sistema',
 *   loadComponent: () => import('@shared/components/submodules-container/submodules-container.component')
 *     .then((m) => m.SubmodulesContainerComponent),
 *   data: {
 *     parentPath: 'sistema',
 *     title: 'Módulos del Sistema',
 *     subtitle: 'Seleccione un módulo para gestionar su configuración'
 *   },
 *   children: [...]
 * }
 * ```
 */
@Component({
  selector: 'app-submodules-container',
  standalone: true,
  imports: [RouterOutlet, CommonModule, SubmodulesGridComponent, BreadcrumbComponent],
  template: `
    <div class="submodules-container p-6">
      @if (!hasActiveChildRoute()) {
        <app-breadcrumb [items]="breadcrumbItems()" />
        
        <app-submodules-grid 
          [childRoutes]="childRoutes()"
          [title]="_title()"
          [subtitle]="_subtitle()"
          [carouselMode]="carouselMode">
        </app-submodules-grid>
      } @else {
        <router-outlet></router-outlet>
      }
    </div>
  `,
  styles: [`
    .submodules-container {
      width: 100%;
      min-height: 100%;
    }
  `]
})
export class SubmodulesContainerComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private sessionService = inject(SessionService);
  private permissionService = inject(PermissionService);

  @Input() parentPath?: string; 
  @Input() routes?: Routes; 
  
  _title = signal<string>('Módulos Disponibles');
  @Input() set title(val: string) { this._title.set(val); }
  get title() { return this._title(); }

  _subtitle = signal<string>('Seleccione un módulo para gestionar su configuración');
  @Input() set subtitle(val: string) { this._subtitle.set(val); }
  get subtitle() { return this._subtitle(); }

  parentBreadcrumbTitle = signal<string | null>(null);
  parentBreadcrumbUrl = signal<string | null>(null);

  /** Carrusel infinito (p. ej. Configuración global); se activa con route.data.submodulesCarousel */
  carouselMode = false;

  childRoutes = signal<Route[]>([]);
  
  breadcrumbItems = computed<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [];
    if (this.parentBreadcrumbTitle()) {
      items.push({ 
        label: this.parentBreadcrumbTitle()!, 
        url: this.parentBreadcrumbUrl() || undefined 
      });
    }
    items.push({ label: this._title() });
    return items;
  });

  constructor() {
    const initialData = this.activatedRoute.snapshot.data;
    if (initialData?.['submodulesCarousel'] === true) {
      this.carouselMode = true;
    }
    // Efecto para detectar cambios en la ruta activa
    effect(() => {
      const routeData = this.activatedRoute.snapshot.data;
      if (routeData && routeData['parentPath'] && !this.parentPath) {
        this.parentPath = routeData['parentPath'];
      }
      if (routeData && routeData['title']) {
        this._title.set(routeData['title']);
      }
      if (routeData && routeData['subtitle']) {
        this._subtitle.set(routeData['subtitle']);
      }
      if (routeData && routeData['parentBreadcrumbTitle']) {
        this.parentBreadcrumbTitle.set(routeData['parentBreadcrumbTitle']);
      }
      if (routeData && routeData['parentBreadcrumbUrl']) {
        this.parentBreadcrumbUrl.set(routeData['parentBreadcrumbUrl']);
      }
      if (routeData && routeData['submodulesCarousel'] === true) {
        this.carouselMode = true;
      }
    });
  }

  hasActiveChildRoute = () => {
    return this.activatedRoute.firstChild !== null;
  };

  ngOnInit(): void {
    // Cargar desde route.data primero
    const routeData = this.activatedRoute.snapshot.data;
    if (routeData && routeData['parentPath'] !== undefined) {
      this.parentPath = routeData['parentPath'];
    }
    if (routeData && routeData['title']) {
      this._title.set(routeData['title']);
    }
    if (routeData && routeData['subtitle']) {
      this._subtitle.set(routeData['subtitle']);
    }
    if (routeData && routeData['parentBreadcrumbTitle']) {
      this.parentBreadcrumbTitle.set(routeData['parentBreadcrumbTitle']);
    }
    if (routeData && routeData['parentBreadcrumbUrl']) {
      this.parentBreadcrumbUrl.set(routeData['parentBreadcrumbUrl']);
    }
    if (routeData && routeData['routes']) {
      const routesValue = routeData['routes'];
      // Si es una función, ejecutarla para obtener las rutas
      if (typeof routesValue === 'function') {
        this.routes = routesValue();
      } else {
        this.routes = routesValue;
      }
    }
    if (routeData && routeData['submodulesCarousel'] === true) {
      this.carouselMode = true;
    }

    this.loadChildRoutes();
  }

  /**
   * Carga las rutas hijas del path padre especificado
   * Maneja dos casos:
   * 1. Rutas hijas (children) - cuando la ruta tiene children definidos
   * 2. Rutas hermanas - cuando estamos en path: '' y queremos mostrar las rutas del mismo nivel
   */
  private loadChildRoutes(): void {
    const currentRoute = this.activatedRoute.snapshot;
    const currentPath = currentRoute.routeConfig?.path || '';
    
    // Caso 1: Si la ruta actual tiene children, usarlas directamente
    if (currentRoute.routeConfig && currentRoute.routeConfig.children) {
      this.childRoutes.set(this.filterRoutesByRole(currentRoute.routeConfig.children));
      return;
    }

    // Caso 2: Si tenemos rutas pasadas como input, usarlas directamente (para path: '')
    if (this.routes && this.routes.length > 0) {
      // Filtrar la ruta actual (path: '')
      const siblings = this.routes.filter(
        route => route.path !== '' && route.path !== undefined
      );
      if (siblings.length > 0) {
        this.childRoutes.set(this.filterRoutesByRole(siblings));
        return;
      }
    }

    // Caso 2b: Si estamos en path: '' o parentPath es '', obtener las rutas hermanas
    // Estas están en el mismo array de rutas del módulo
    if (currentPath === '' || this.parentPath === '') {
      // Buscar el parent route que tiene loadChildren (el que carga este módulo)
      let parent = currentRoute.parent;
      while (parent) {
        const parentConfig = parent.routeConfig;
        
        // Si encontramos un parent con loadChildren, buscar ese path en Router.config
        if (parentConfig && parentConfig.loadChildren) {
          // Obtener el path del parent
          const parentPath = parentConfig.path || '';
          if (parentPath) {
            const foundRoute = this.findRouteInConfig(parentPath);
            if (foundRoute && foundRoute.loadChildren) {
              // Para rutas lazy-loaded, necesitamos una solución diferente
              // Intentar obtener desde el Router después de que se cargue
              this.loadSiblingRoutesFromParent(parentPath);
              return;
            }
          }
        }
        
        // Si el parent tiene children directamente, obtener las rutas hermanas
        if (parentConfig && parentConfig.children) {
          const siblings = parentConfig.children.filter(
            route => route.path !== '' && route.path !== undefined && route.path !== currentPath
          );
          if (siblings.length > 0) {
            this.childRoutes.set(this.filterRoutesByRole(siblings));
            return;
          }
        }
        
        parent = parent.parent;
      }
    }

    // Caso 3: Buscar por parentPath en Router.config (solo si no es vacío)
    if (this.parentPath && this.parentPath !== '') {
      const foundRoute = this.findRouteInConfig(this.parentPath);
      if (foundRoute && foundRoute.children) {
        this.childRoutes.set(this.filterRoutesByRole(foundRoute.children));
        return;
      }
    }

    // Caso 4: Intentar obtener desde la URL actual
    const urlSegments = this.router.url.split('/').filter(p => p);
    if (urlSegments.length > 0) {
      const lastSegment = urlSegments[urlSegments.length - 1];
      const foundRoute = this.findRouteInConfig(lastSegment);
      if (foundRoute && foundRoute.children) {
        this.childRoutes.set(this.filterRoutesByRole(foundRoute.children));
        return;
      }
    }

    // Si no encontramos nada, usar array vacío
    this.childRoutes.set([]);
  }

  /**
   * Intenta cargar las rutas hermanas desde un módulo lazy-loaded
   * Espera a que el Router resuelva las rutas y luego las obtiene
   */
  private async loadSiblingRoutesFromParent(parentPath: string): Promise<void> {
    // Esperar un tick para que el Router resuelva las rutas lazy-loaded
    await new Promise(resolve => setTimeout(resolve, 0));
    
    // Buscar la ruta en Router.config después de que se haya cargado
    const foundRoute = this.findRouteInConfig(parentPath);
    
    if (foundRoute && foundRoute.loadChildren) {
      // Si la ruta usa loadChildren, intentar obtener las rutas resueltas
      // Esto funciona porque Angular resuelve loadChildren cuando se navega
      try {
        const routesModule = await foundRoute.loadChildren();
        if (routesModule && Array.isArray(routesModule)) {
          // Filtrar la ruta actual (path: '')
          const siblings = routesModule.filter(
            route => route.path !== '' && route.path !== undefined
          );
          if (siblings.length > 0) {
            this.childRoutes.set(this.filterRoutesByRole(siblings));
            return;
          }
        }
      } catch (error) {
        console.warn('No se pudieron cargar las rutas lazy-loaded:', error);
      }
    }
    
    // Si loadChildren no funciona, intentar obtener desde Router después de navegación
    // Esto requiere que el Router ya haya resuelto las rutas
    setTimeout(() => {
      const allRoutes = this.router.config;
      const configRoute = allRoutes.find(r => r.path === parentPath);
      if (configRoute) {
        // Las rutas ya deberían estar resueltas en este punto
        // Intentar obtener desde el parent route activo
        const parentRoute = this.activatedRoute.parent;
        if (parentRoute && parentRoute.routeConfig) {
          // Esto no funcionará directamente porque loadChildren no expone las rutas así
          // Necesitamos otra estrategia
        }
      }
    }, 100);
  }

  /**
   * Busca una ruta en Router.config, incluyendo rutas lazy-loaded
   */
  private findRouteInConfig(path: string): Route | null {
    return this.findRouteByPath(this.router.config, path);
  }

  /**
   * Busca una ruta recursivamente en la configuración
   */
  private findRouteByPath(routes: Routes, path: string): Route | null {
    for (const route of routes) {
      if (route.path === path) {
        return route;
      }
      if (route.children) {
        const found = this.findRouteByPath(route.children, path);
        if (found) return found;
      }
      // También buscar en loadChildren si es una función
      if (route.loadChildren) {
        // No podemos resolver loadChildren aquí, pero podemos buscar en la estructura
      }
    }
    return null;
  }

  /**
   * Filtra rutas según los roles definidos en route.data.roles y el usuario autenticado
   */
  private filterRoutesByRole(routes: Route[]): Route[] {
    return routes.filter((route) => {
      const permission = (route.data && (route.data as any).permission) as
        { modulo?: string; subModulo?: string } | undefined;
      if (permission?.modulo && permission?.subModulo) {
        return this.permissionService.tieneAccesoSubModulo(permission.modulo, permission.subModulo);
      }
      if (permission?.modulo) {
        return this.permissionService.tieneAccesoModulo(permission.modulo);
      }

      const roles = (route.data && (route.data as any).roles) as string[] | undefined;
      if (!roles || roles.length === 0) {
        return true;
      }
      return this.sessionService.hasAnyRole(roles);
    });
  }
}

